// Age Category API functions for tournaments.
import secureApi from '../../utils/secureApi';
import imageCompression from 'browser-image-compression';

function getCompressionOptions(file: File) {
  const sizeKB = file.size / 1024;
  const maxWidthOrHeight = 1600;
  if (sizeKB < 120) {
    return null;
  } else {
    return { maxSizeMB: 0.15, maxWidthOrHeight, initialQuality: 0.92, useWebWorker: true };
  }
}

export async function getAgeCategoriesByTournamentId(tournamentId: string, sport?: string) {
  const params: Record<string, string> = { tournament: tournamentId };
  if (sport) {
    params.sport = sport;
  }
  const response = await secureApi.get<any>(
    '/api/v2/tournaments/admin/age-category/get-all-categories',
    { params }
  );
  
  // Handle API response structure: { statusCode: 200, status: "success", data: {...} }
  if (response && response.data) {
    return response.data;
  }
  return response;
}

export async function getAllAgeCategoriesPaginated(tournamentId: string, page = 1, pageSize = 10, search = '', type = '', format = '', tournamentSeason?: string, sport?: string) {
  const params: any = { page, pageSize, search, type, format };
  
  // Always include tournament
  params.tournament = tournamentId;
  
  // If tournamentSeason is provided, also include it
  if (tournamentSeason) {
    params.tournamentSeason = tournamentSeason;
  }
  
  // If sport is provided, also include it
  if (sport) {
    params.sport = sport;
  }
  
  const response = await secureApi.get<any>(
    '/api/v2/tournaments/admin/age-category/get-all-categories',
    { params }
  );
  
  // Handle API response structure: { statusCode: 200, status: "success", data: {...} }
  if (response && response.data && (response.data.paginatedResults || response.data.totalCount !== undefined)) {
    return response.data;
  } else if (response && (response.paginatedResults || response.totalCount !== undefined)) {
    return response;
  }
  return response;
}

export async function uploadAgeCategoryImage(file: File, type: 'liveImage' | 'highlightsImage'): Promise<string> {
  let processedFile = file;
  const options = getCompressionOptions(file);
  if (options) {
    processedFile = await imageCompression(file, options);
  }
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yy = String(now.getFullYear()).slice(-2);
  const ext = processedFile.type.split('/')[1] || 'jpg';
  const fileName = `age_category_${Date.now()}.${ext}`;
  let context = '';
  if (type === 'liveImage') {
    context = `live-matches-banners/${dd}-${mm}-${yy}`;
  } else {
    context = `highlights-banners/${dd}-${mm}-${yy}`;
  }
  const presignRes = await secureApi.post<any>(
    '/api/v1/general/presigned-url',
    {
      fileName,
      contentType: processedFile.type,
      context,
    }
  );
  const presignedUrl = presignRes?.data?.presignedUrl ?? presignRes?.presignedUrl;
  const s3Key = presignRes?.data?.key ?? presignRes?.key;
  if (!presignedUrl || !s3Key) {
    throw new Error('Failed to get presigned URL');
  }
  const uploadRes = await fetch(presignedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': processedFile.type },
    body: processedFile,
  });
  if (!uploadRes.ok) {
    throw new Error('Failed to upload image to S3');
  }
  return s3Key;
}

export async function addAgeCategory(payload: any, sport?: string) {
  let url = '/api/v2/tournaments/admin/age-category/add';
  if (sport) {
    url += `?sport=${encodeURIComponent(sport)}`;
  }
  return await secureApi.post<any>(url, payload);
}

export async function editAgeCategory(id: string, payload: any, sport?: string) {
  let url = `/api/v2/tournaments/admin/age-category/edit/${id}`;
  if (sport) {
    url += `?sport=${encodeURIComponent(sport)}`;
  }
  return await secureApi.post<any>(url, payload);
}

export async function deleteAgeCategory(id: string, sport?: string) {
  let url = `/api/v2/tournaments/admin/age-category/delete/${id}`;
  if (sport) {
    url += `?sport=${encodeURIComponent(sport)}`;
  }
  return await secureApi.post<any>(url, {});
}

/**
 * Get age category by ID
 * @param id - Age Category ID
 * @param sport - Sport type (optional, included as query parameter)
 * @returns Promise with the age category data
 */
export async function getAgeCategoryById(id: string, sport?: string) {
  let url = `/api/v2/tournaments/admin/age-category/get-by-id/${id}`;
  if (sport) {
    url += `?sport=${encodeURIComponent(sport)}`;
  }
  const response = await secureApi.get<any>(url);
  
  // Handle API response structure: { statusCode: 200, status: "success", data: {...} }
  if (response && response.data) {
    return response.data;
  }
  return response;
}

/**
 * Register an age category to a tournament season
 * @param tournamentId - Tournament ID (included in URL)
 * @param sport - Sport type (e.g., 'Football', 'Badminton') - included as query parameter
 * @param payload - Age category registration data
 * @returns Promise with the registered age category
 */
export async function registerAgeCategory(
  tournamentId: string,
  sport: string,
  payload: {
    ageCategory: string;
    format: string;
    sports: string;
    standingsType: string;
    tournamentSeason: string;
  }
) {
  const response = await secureApi.post<any>(
    `/api/v2/tournaments/register-age-category/${tournamentId}?sport=${encodeURIComponent(sport)}`,
    payload
  );
  
  // Handle API response structure: { statusCode: 200, status: "success", data: {...} }
  if (response && response.data) {
    return response.data;
  }
  return response;
} 